import React, { useState, useCallback } from 'react';
import Select from 'react-select';
import styled from '@emotion/styled';
import type { TTSConfig, PollyTTSConfig } from '@trinity/chat';
import { EditActionButtons } from '../presentation/EditActionButtons';

type VoiceOption = { value: string; label: string };

// Only voices confirmed to support the Polly neural engine.
const POLLY_VOICE_OPTIONS: VoiceOption[] = [
  { value: 'Joanna',   label: 'Joanna — US English, Female' },
  { value: 'Salli',    label: 'Salli — US English, Female' },
  { value: 'Kendra',   label: 'Kendra — US English, Female' },
  { value: 'Kimberly', label: 'Kimberly — US English, Female' },
  { value: 'Matthew',  label: 'Matthew — US English, Male' },
  { value: 'Amy',      label: 'Amy — British English, Female' },
  { value: 'Emma',     label: 'Emma — British English, Female' },
  { value: 'Brian',    label: 'Brian — British English, Male' },
];

// ---------------------------------------------------------------------------
// Default — Polly/Joanna/neural is the platform default
// ---------------------------------------------------------------------------

const DEFAULT_POLLY_CONFIG: PollyTTSConfig = {
  type: 'polly',
  settings: {
    voiceId: 'Joanna',
    engine: 'neural',
    outputFormat: 'pcm',
    textType: 'text',
  },
};

export type VoiceSettingsProps = {
  /** Current saved TTS config for this segment, or null if none configured yet. */
  value: TTSConfig | null;
  /** Called with the full TTSConfig when the user saves. */
  onSave: (config: TTSConfig) => Promise<void>;
  isSaving?: boolean;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
  /** Current voice ID displayed in the dropdown. Controlled by the parent. */
  voiceId: string;
  /** Called whenever the user changes the voice selection (edit or cancel). */
  onVoiceIdChange: (id: string) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
  max-width: 600px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const Description = styled.p`
  font-size: 14px;
  color: #666;
  margin: 4px 0 0 0;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

export function VoiceSettings({
  value,
  onSave,
  isSaving = false,
  whitelabelEnabled,
  primaryColor,
  voiceId,
  onVoiceIdChange,
}: VoiceSettingsProps): React.ReactElement {
  // Resolve the active Polly config — fall back to default if nothing saved yet
  // or if a non-Polly config is stored (future-proofing).
  const resolvedInitial: PollyTTSConfig =
    value?.type === 'polly' ? value : DEFAULT_POLLY_CONFIG;

  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    onVoiceIdChange(resolvedInitial.settings.voiceId);
    setIsEditing(false);
  }, [onVoiceIdChange, resolvedInitial.settings.voiceId]);

  const handleSave = useCallback(async () => {
    const config: PollyTTSConfig = {
      type: 'polly',
      settings: {
        voiceId,
        engine: 'neural',
        outputFormat: 'pcm',
        textType: 'text',
      },
    };
    await onSave(config);
    setIsEditing(false);
  }, [voiceId, onSave]);

  const hasChanges = voiceId !== resolvedInitial.settings.voiceId;

  // Match focused background to whitelabel color, falling back to brand blue
  const focusedBg = whitelabelEnabled && primaryColor ? primaryColor : '#28aae1';

  const selectedVoice = POLLY_VOICE_OPTIONS.find(o => o.value === voiceId) ?? null;

  return (
    <Container>
      <div>
        <Header>
          <SectionTitle>Voice Settings</SectionTitle>
          <EditActionButtons
            isEditing={isEditing}
            isSaving={!hasChanges || isSaving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            themedColor={whitelabelEnabled && primaryColor ? primaryColor : undefined}
          />
        </Header>
        <Description>
          Configure the text-to-speech voice for this segment&apos;s Digital Twin
        </Description>
      </div>

      <FieldsContainer>
        <Field>
          <FieldLabel>Voice</FieldLabel>
          <Select<VoiceOption>
            options={POLLY_VOICE_OPTIONS}
            value={selectedVoice}
            onChange={option => { if (option) onVoiceIdChange(option.value); }}
            isDisabled={!isEditing}
            menuPortalTarget={document.body}
            styles={{
              control: base => ({ ...base, minHeight: '40px' }),
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? focusedBg : 'white',
                color: state.isFocused ? 'white' : '#4c4c4c',
                cursor: 'pointer',
              }),
            }}
          />
        </Field>
      </FieldsContainer>
    </Container>
  );
}
